import { useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { EmptyState } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { GroupsService } from '_common/services';
import { paths } from '_types/api';

import { listObjects } from '_common/components/Table/TableSlice';

import { GroupAvatar } from '_common/components';
import {
  ThunksTable,
  Cell,
  Footer,
  SelectionCounter,
  type TableColumn,
  type TableRow,
} from '_common/components/Table';
import PermissionsCell from './PermissionsCell';
import MemberCell from './MemberCell';
import UserNameCell from 'Storage/pages/RecyclePage/Table/UserNameCell';

import styles from '../GroupsPage.module.scss';

const PAGE_IDENTITY = 'groups';

type TableProps = {
  onPermissionsChange: (
    permissions: paths['/api/object/group/list']['get']['responses']['200']['content']['multipart/form-data']['permissions'],
  ) => void;
};

const Table = ({ onPermissionsChange }: TableProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const list = useSelector((state) => state.table.identity.groups.list);
  const appData = useSelector((state) => state.app.data);

  //#region Table object listing handlers
  const fetchObjects = useCallback(async (parameters: any) => {
    const resp = await new GroupsService().getGroupsList(parameters);
    onPermissionsChange(resp.data.permissions);
    return resp;
  }, []);

  useEffect(() => {
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, []);

  // No useEffectOnUpdate since fetchObjects doesn't have any dependencies
  // Add if dependencies are added to fetchObjects

  //#endregion

  const columns = useMemo<TableColumn[]>(() => {
    return [
      {
        id: 'name',
        accessorKey: 'name',
        minWidth: 296,
        header: intl.formatMessage({ id: 'groups.header.name' }),
        meta: { bodyClassName: 'py-4' },
        cell: (info) => {
          const object = appData[info.row.original.id];

          return (
            <Cell testId={`${object.id}-name-column`}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                <div style={{ margin: '0 2rem 0 0' }}>
                  <GroupAvatar
                    text={object.name.substring(0, 2).toLowerCase()}
                    size="m"
                    color="blue"
                  />
                </div>
                <div className={`${styles.name}`}>{object.name}</div>
              </div>
            </Cell>
          );
        },
      },
      {
        id: 'permissions',
        accessorKey: 'permissions',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'groups.header.permissions' }),
        cell: (info) => {
          const object = appData[info.row.original.id];
          return (
            <Cell testId={`${object.id}-permissions-column`}>
              <PermissionsCell object={object} />
            </Cell>
          );
        },
      },
      {
        id: 'owner',
        accessorKey: 'owner',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'groups.header.owner' }),
        cell: (info) => {
          const object = appData[info.row.original.id];
          return (
            <Cell testId={`${object.id}-owner-column`}>
              <UserNameCell userId={object.owner} />
            </Cell>
          );
        },
      },
      {
        id: 'member',
        accessorKey: 'member',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'groups.header.member' }, { membersCount: 2 }),
        cell: (info) => {
          const object = appData[info.row.original.id];
          return (
            <Cell testId={`${object.id}-members-column`}>
              <MemberCell object={object} />
            </Cell>
          );
        },
      },
    ];
  }, [appData]);

  const rowsData = useMemo<TableRow[]>(() => {
    return list.map<TableRow>((objectId) => {
      return appData[objectId];
    });
  }, [list, appData]);

  if (list.length === 0) {
    return (
      <div className={styles.emptyView}>
        <EmptyState
          size="l"
          icon="users"
          title={intl.formatMessage({ id: 'groups.emptyView.title' })}
          description={intl.formatMessage({ id: 'groups.emptyView.subtitle' })}
          testId="groups-empty-list"
        />
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <ThunksTable
        identity={PAGE_IDENTITY}
        fetchObjects={fetchObjects}
        options={{ columns, data: rowsData }}
        lazyLoading
        renderFooter={() => (
          <Footer>
            <SelectionCounter identity={PAGE_IDENTITY} />
          </Footer>
        )}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        testId="groups"
      />
    </div>
  );
};

export default Table;
